import { useRef, type KeyboardEvent } from 'react';
import type { ImportBatchRow, RowChangeSet, RowChoice } from '@discovr/types';
import { cn } from '../../../lib/utils';
import { rowChoice } from './reviewRowStatus';
import { decisionsChangeSet } from './reviewGridDraft';

type PickableChoice = Exclude<RowChoice, 'undecided'>;

interface VersionOption {
  choice: PickableChoice;
  label: string;
}

const VERSION_OPTIONS: readonly VersionOption[] = [
  { choice: 'current', label: 'Current' },
  { choice: 'sheet', label: 'Sheet' },
];

// ARIA radio pattern: arrows move to the neighbouring option and select it.
const ARROW_STEP: Readonly<Record<string, number>> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };

// Undecided rows block the import, so the toggle itself asks for attention.
const NEEDS_DECISION_CLASS = 'border-border-warning-default';

interface VersionToggleProps {
  row: ImportBatchRow;
  busy: boolean;
  onSaveRows: (changes: RowChangeSet, message: string) => void;
}

function decisionMessage(rowNumber: number, option: VersionOption): string {
  return `Row ${rowNumber} set to ${option.label.toLowerCase()} version`;
}

export function VersionToggle({ row, busy, onSaveRows }: VersionToggleProps) {
  const choice = rowChoice(row);
  const undecided = choice === 'undecided';
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  // Roving tabindex: one tab stop, on the chosen option (or the first when
  // nothing is chosen yet).
  const checkedIndex = VERSION_OPTIONS.findIndex((option) => option.choice === choice);
  const tabStopIndex = Math.max(checkedIndex, 0);

  function pick(option: VersionOption) {
    if (busy || option.choice === choice) return;
    onSaveRows(decisionsChangeSet([{ rowNumber: row.rowNumber, choice: option.choice }]), decisionMessage(row.rowNumber, option));
  }

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const step = ARROW_STEP[event.key];
    if (step === undefined || busy) return;
    event.preventDefault();
    const next = (index + step + VERSION_OPTIONS.length) % VERSION_OPTIONS.length;
    const option = VERSION_OPTIONS[next];
    if (option === undefined) return;
    optionRefs.current[next]?.focus();
    pick(option);
  }

  return (
    <div
      role="radiogroup"
      aria-label={`Version for row ${row.rowNumber}`}
      className={cn('inline-flex overflow-hidden rounded-full border border-border bg-card', undecided && NEEDS_DECISION_CLASS)}
    >
      {VERSION_OPTIONS.map((option, index) => {
        const checked = option.choice === choice;
        return (
          <button
            key={option.choice}
            ref={(node) => {
              optionRefs.current[index] = node;
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={index === tabStopIndex ? 0 : -1}
            // aria-disabled, not disabled: an arrow key focuses the option and
            // saves, and a disabled button would drop that focus mid-save.
            aria-disabled={busy}
            onClick={() => pick(option)}
            onKeyDown={(event) => onKeyDown(event, index)}
            className={cn(
              'inline-flex h-7 items-center px-2.5 text-caption-medium transition-colors aria-disabled:cursor-not-allowed aria-disabled:opacity-50',
              index > 0 && 'border-l border-border',
              checked ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
